/**
 * RichTextField — TipTap WYSIWYG editor component for ReactiveJSON.
 *
 * Main module: wires TipTap to the ReactiveJSON data layer, defines
 * custom nodes (Callout, Details, Columns, InlineDate, CodeBlock),
 * and assembles the extension stack via buildExtensions().
 *
 * Satellite modules (same directory):
 *   rich-text-constants.jsx — shared constants & helpers
 *   slash-commands.jsx      — `/` command system
 *   emoji-suggestion.jsx    — `:` emoji picker
 *   status-badge-node.jsx   — inline status badge atom
 *   EditorMenus.jsx         — BubbleMenu / FloatingMenu / DragHandle
 *
 * @see docs/frontend/docspace-tiptap-editeur-riche.md
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';
import { EditorContent, NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor } from '@tiptap/react';
import { Extension, Node, mergeAttributes, nodeInputRule } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import ImageResize from 'tiptap-extension-resize-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Typography from '@tiptap/extension-typography';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { FileHandler } from '@tiptap/extension-file-handler';
import Emoji from '@tiptap/extension-emoji';
import Mention from '@tiptap/extension-mention';
import { Calendar, Check, ChevronRight, Copy } from 'lucide-react';

import useEditorFileUpload from '../hooks/useEditorFileUpload';
import useOrphanFileCleanup from '../hooks/useOrphanFileCleanup';
import {
    lowlight,
    slugify,
    CALLOUT_TYPES,
    CODE_LANGUAGES,
    COLUMN_LAYOUTS,
    DEFAULT_FEATURES,
} from './rich-text-constants';
import { SlashCommands, createSlashSuggestionRenderer, setSlashContext } from './slash-commands';
import { buildEmojiDefaults, createEmojiSuggestionRenderer } from './emoji-suggestion';
import StatusBadgeNode from './status-badge-node';
import EditorMenus from './EditorMenus';
import styles from './RichTextField.module.css';
import { searchUsers } from '../../../utils/decoupled';

/* ================================================================
   @mention user search (module-level cache)
   ================================================================ */

const _mentionCache = {};

async function fetchMentionUsers(query) {
    const key = (query || '').toLowerCase().trim();
    if (_mentionCache[key]) return _mentionCache[key];
    try {
        const items = (await searchUsers(key, 8)).map((u) => ({ id: u.uid, label: u.name }));
        _mentionCache[key] = items;
        return items;
    } catch {
        return [];
    }
}

function _escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function createMentionSuggestionRenderer() {
    let popup = null;
    let currentProps = null;
    let activeIndex = 0;

    function destroy() {
        if (popup) { popup.remove(); popup = null; }
        currentProps = null;
        activeIndex = 0;
    }

    function renderItems() {
        if (!popup || !currentProps) return;
        const { items, clientRect } = currentProps;

        if (clientRect) {
            const rect = typeof clientRect === 'function' ? clientRect() : clientRect;
            if (rect) {
                popup.style.left = `${rect.left}px`;
                popup.style.top = `${rect.bottom + 4}px`;
            }
        }

        if (!items || items.length === 0) {
            popup.innerHTML = '<div class="bg-white border border-stone-200 rounded-lg shadow-lg py-2 px-3 text-xs text-stone-400">Aucun utilisateur trouvé</div>';
            return;
        }

        popup.innerHTML = `<div class="bg-white border border-stone-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto min-w-[180px]">
            ${items.map((item, i) => `<div data-mention-item="${i}" class="px-3 py-1.5 text-sm cursor-pointer transition-colors ${i === activeIndex ? 'bg-blue-50 text-blue-700 font-medium' : 'text-stone-700 hover:bg-stone-50'}"><span class="text-blue-500 mr-0.5">@</span>${_escapeHtml(item.label)}</div>`).join('')}
        </div>`;

        popup.querySelectorAll('[data-mention-item]').forEach((el) => {
            el.addEventListener('mousedown', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const idx = parseInt(el.dataset.mentionItem, 10);
                if (items[idx] && currentProps?.command) currentProps.command(items[idx]);
            });
        });
    }

    return {
        onStart(props) {
            currentProps = props;
            activeIndex = 0;
            popup = document.createElement('div');
            popup.className = 'mention-popup';
            popup.style.position = 'fixed';
            popup.style.zIndex = '10000';
            document.body.appendChild(popup);
            renderItems();
        },
        onUpdate(props) {
            currentProps = props;
            activeIndex = 0;
            renderItems();
        },
        onKeyDown({ event }) {
            if (!popup || !currentProps) return false;
            const items = currentProps.items || [];
            if (event.key === 'Escape') { destroy(); return true; }
            if (event.key === 'ArrowDown') { if (items.length === 0) return true; event.preventDefault(); activeIndex = Math.min(activeIndex + 1, items.length - 1); renderItems(); return true; }
            if (event.key === 'ArrowUp') { if (items.length === 0) return true; event.preventDefault(); activeIndex = Math.max(activeIndex - 1, 0); renderItems(); return true; }
            if (event.key === 'Enter' || event.key === 'Tab') {
                if (items.length > 0 && items[activeIndex]) { event.preventDefault(); currentProps.command(items[activeIndex]); return true; }
                return false;
            }
            return false;
        },
        onExit() { destroy(); },
    };
}

/* ================================================================
   Custom extensions — HeadingAnchors
   ================================================================ */

const HeadingAnchors = Extension.create({
    name: 'headingAnchors',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('headingAnchors'),
                props: {
                    decorations: (state) => {
                        const decorations = [];
                        const seen = {};
                        state.doc.descendants((node, pos) => {
                            if (node.type.name === 'heading') {
                                const base = slugify(node.textContent);
                                seen[base] = (seen[base] || 0) + 1;
                                const id = seen[base] > 1 ? `${base}-${seen[base] - 1}` : base;
                                decorations.push(
                                    Decoration.node(pos, pos + node.nodeSize, { id })
                                );
                            }
                        });
                        return DecorationSet.create(state.doc, decorations);
                    },
                },
            }),
        ];
    },
});

/* ================================================================
   Callout block extension + NodeView
   ================================================================ */

const CalloutNode = Node.create({
    name: 'callout',
    group: 'block',
    content: 'block+',
    defining: true,

    addAttributes() {
        return {
            type: {
                default: 'info',
                parseHTML: (el) => el.getAttribute('data-callout-type') || 'info',
                renderHTML: (attrs) => ({ 'data-callout-type': attrs.type }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-callout-type]' }];
    },

    renderHTML({ node, HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes, { 'data-callout-type': node.attrs.type }), 0];
    },

    addCommands() {
        return {
            insertCallout: (attrs) => ({ commands }) =>
                commands.insertContent({
                    type: this.name,
                    attrs,
                    content: [{ type: 'paragraph' }],
                }),
        };
    },

    addNodeView() {
        return ReactNodeViewRenderer(CalloutView);
    },
});

function CalloutView({ node, updateAttributes }) {
    const type = node.attrs.type;
    const config = CALLOUT_TYPES[type] || CALLOUT_TYPES.info;

    return (
        <NodeViewWrapper className="not-prose">
            <div className={`flex gap-3 p-4 rounded-xl border ${config.classes} my-4`}>
                <div className="flex-shrink-0 pt-0.5" contentEditable={false}>
                    <select
                        value={type}
                        onChange={(e) => updateAttributes({ type: e.target.value })}
                        className="text-sm bg-transparent border-none outline-none cursor-pointer font-medium"
                    >
                        {Object.entries(CALLOUT_TYPES).map(([key, val]) => (
                            <option key={key} value={key}>{val.label}</option>
                        ))}
                    </select>
                </div>
                <div className="flex-1 min-w-0 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                    <NodeViewContent />
                </div>
            </div>
        </NodeViewWrapper>
    );
}

/* ================================================================
   Details (accordion) block extension + NodeView
   ================================================================ */

const DetailsNode = Node.create({
    name: 'detailsBlock',
    group: 'block',
    content: 'block+',
    defining: true,

    addAttributes() {
        return {
            summary: {
                default: 'Cliquer pour développer',
                parseHTML: (el) => {
                    const s = el.querySelector('summary');
                    return s ? s.textContent : 'Détails';
                },
            },
        };
    },

    parseHTML() {
        return [{ tag: 'details' }];
    },

    renderHTML({ node, HTMLAttributes }) {
        return ['details', mergeAttributes(HTMLAttributes, { open: '' }), ['summary', {}, node.attrs.summary], ['div', {}, 0]];
    },

    addCommands() {
        return {
            insertDetails: (attrs) => ({ commands }) =>
                commands.insertContent({
                    type: this.name,
                    attrs,
                    content: [{ type: 'paragraph' }],
                }),
        };
    },

    addNodeView() {
        return ReactNodeViewRenderer(DetailsView);
    },
});

function DetailsView({ node, updateAttributes }) {
    const [open, setOpen] = useState(true);

    return (
        <NodeViewWrapper className="not-prose">
            <div className="border border-stone-200 rounded-xl overflow-hidden my-4">
                <div
                    className="flex items-center gap-2 px-4 py-3 bg-stone-50 border-b border-stone-100"
                    contentEditable={false}
                >
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        className="text-stone-400 hover:text-stone-600 transition-colors p-0.5"
                    >
                        <ChevronRight size={16} className={`transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
                    </button>
                    <input
                        value={node.attrs.summary}
                        onChange={(e) => updateAttributes({ summary: e.target.value })}
                        className="font-medium text-sm text-stone-700 bg-transparent border-none outline-none flex-1"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
                <div className={`px-4 py-3 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${open ? '' : 'hidden'}`}>
                    <NodeViewContent />
                </div>
            </div>
        </NodeViewWrapper>
    );
}

/* ================================================================
   Columns layout extension + NodeView
   ================================================================ */

const ColumnNode = Node.create({
    name: 'column',
    group: 'columns_child',
    content: 'block+',
    defining: true,
    isolating: true,

    parseHTML() {
        return [{ tag: 'div[data-column]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes, { 'data-column': '' }), 0];
    },
});

const ColumnsNode = Node.create({
    name: 'columns',
    group: 'block',
    content: 'column{2,4}',
    defining: true,
    isolating: true,

    addAttributes() {
        return {
            columns: {
                default: 2,
                parseHTML: (el) => parseInt(el.getAttribute('data-columns'), 10) || 2,
                renderHTML: (attrs) => ({ 'data-columns': attrs.columns }),
            },
            layout: {
                default: 'equal-2',
                parseHTML: (el) => el.getAttribute('data-layout') || 'equal-2',
                renderHTML: (attrs) => ({ 'data-layout': attrs.layout }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-columns]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['div', HTMLAttributes, 0];
    },

    addCommands() {
        const equalLayout = (n) => {
            if (n === 4) return 'equal-4';
            if (n === 3) return 'equal-3';
            return 'equal-2';
        };

        const findColumnsNode = ($from) => {
            for (let d = $from.depth; d > 0; d--) {
                const node = $from.node(d);
                if (node.type.name === 'columns') return { node, depth: d };
            }
            return null;
        };

        return {
            insertColumns: (attrs = {}) => ({ commands }) => {
                const cols = attrs.columns || 2;
                const layout = attrs.layout || equalLayout(cols);
                return commands.insertContent({
                    type: this.name,
                    attrs: { columns: cols, layout },
                    content: Array.from({ length: cols }, () => ({
                        type: 'column',
                        content: [{ type: 'paragraph' }],
                    })),
                });
            },
            addColumn: () => ({ state, dispatch }) => {
                const found = findColumnsNode(state.selection.$from);
                if (!found || found.node.childCount >= 4) return false;
                if (dispatch) {
                    const pos = state.selection.$from.before(found.depth);
                    const insertAt = pos + found.node.nodeSize - 1;
                    const newCol = state.schema.nodes.column.createAndFill();
                    if (!newCol) return false;
                    const newCount = found.node.childCount + 1;
                    const tr = state.tr.insert(insertAt, newCol);
                    tr.setNodeMarkup(pos, undefined, {
                        ...found.node.attrs,
                        columns: newCount,
                        layout: equalLayout(newCount),
                    });
                    dispatch(tr);
                }
                return true;
            },
            removeColumn: () => ({ state, dispatch }) => {
                const found = findColumnsNode(state.selection.$from);
                if (!found || found.node.childCount <= 2) return false;
                if (dispatch) {
                    const colStart = state.selection.$from.before(found.depth);
                    const lastChild = found.node.child(found.node.childCount - 1);
                    const lastColPos = colStart + found.node.nodeSize - 1 - lastChild.nodeSize;
                    const newCount = found.node.childCount - 1;
                    const tr = state.tr.delete(lastColPos, lastColPos + lastChild.nodeSize);
                    // tr.mapping.map() adjusts colStart because the delete
                    // shifted positions before this point in the document.
                    tr.setNodeMarkup(tr.mapping.map(colStart), undefined, {
                        ...found.node.attrs,
                        columns: newCount,
                        layout: equalLayout(newCount),
                    });
                    dispatch(tr);
                }
                return true;
            },
            setColumnsLayout: (layout) => ({ state, dispatch }) => {
                const found = findColumnsNode(state.selection.$from);
                if (!found) return false;
                const targetCols = COLUMN_LAYOUTS[layout]?.cols || 2;
                const currentCols = found.node.childCount;
                if (dispatch) {
                    const colStart = state.selection.$from.before(found.depth);
                    let tr = state.tr;
                    if (targetCols > currentCols) {
                        let insertAt = colStart + found.node.nodeSize - 1;
                        for (let i = 0; i < targetCols - currentCols; i++) {
                            const newCol = state.schema.nodes.column.createAndFill();
                            if (newCol) {
                                tr = tr.insert(insertAt, newCol);
                                insertAt += newCol.nodeSize;
                            }
                        }
                    } else if (targetCols < currentCols) {
                        let nodeAfterOps = found.node;
                        for (let i = 0; i < currentCols - targetCols; i++) {
                            const lastChild = nodeAfterOps.child(nodeAfterOps.childCount - 1);
                            const mappedStart = tr.mapping.map(colStart);
                            const deleteFrom = mappedStart + nodeAfterOps.nodeSize - 1 - lastChild.nodeSize;
                            tr = tr.delete(deleteFrom, deleteFrom + lastChild.nodeSize);
                            nodeAfterOps = tr.doc.nodeAt(mappedStart);
                        }
                    }
                    const mappedPos = tr.mapping.map(colStart);
                    tr.setNodeMarkup(mappedPos, undefined, {
                        ...found.node.attrs,
                        columns: targetCols,
                        layout,
                    });
                    dispatch(tr);
                }
                return true;
            },
            deleteColumns: () => ({ state, dispatch }) => {
                const found = findColumnsNode(state.selection.$from);
                if (!found) return false;
                if (dispatch) {
                    const pos = state.selection.$from.before(found.depth);
                    dispatch(state.tr.delete(pos, pos + found.node.nodeSize));
                }
                return true;
            },
        };
    },

    addNodeView() {
        const LAYOUT_GRID = {
            'equal-2':    '1fr 1fr',
            'equal-3':    '1fr 1fr 1fr',
            'equal-4':    '1fr 1fr 1fr 1fr',
            'left-wide':  '2fr 1fr',
            'right-wide': '1fr 2fr',
            'sidebar':    '1fr 3fr',
        };

        return ({ node, editor, getPos }) => {
            let currentLayout = node.attrs.layout || 'equal-2';
            const cfg = COLUMN_LAYOUTS[currentLayout] || COLUMN_LAYOUTS['equal-2'];

            const focusFirstColumn = () => {
                const pos = getPos();
                if (pos == null) return;
                const resolved = editor.state.doc.resolve(pos + 1);
                editor.commands.setTextSelection(resolved.pos + 1);
                editor.commands.focus();
            };

            const dom = document.createElement('div');
            Object.assign(dom.style, {
                border: '1px dashed #d6d3d1',
                borderRadius: '0.5rem',
                margin: '1.5rem 0',
            });

            const header = document.createElement('div');
            header.contentEditable = 'false';
            Object.assign(header.style, {
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                fontSize: '11px',
                color: '#a8a29e',
                background: '#fafaf9',
                borderBottom: '1px solid #f5f5f4',
                borderRadius: '0.5rem 0.5rem 0 0',
                userSelect: 'none',
            });
            header.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M9 3v18"/></svg><span>${cfg.label}</span>`;
            header.addEventListener('mousedown', (e) => {
                e.preventDefault();
                focusFirstColumn();
            });
            dom.appendChild(header);

            const mql = window.matchMedia('(max-width: 768px)');
            const applyGrid = () => {
                contentDOM.style.gridTemplateColumns = mql.matches ? '1fr' : (LAYOUT_GRID[currentLayout] || '1fr 1fr');
            };

            const contentDOM = document.createElement('div');
            Object.assign(contentDOM.style, {
                display: 'grid',
                gap: '1rem',
                padding: '0.5rem',
            });
            applyGrid();
            mql.addEventListener('change', applyGrid);
            dom.appendChild(contentDOM);

            return {
                dom,
                contentDOM,
                update(updatedNode) {
                    if (updatedNode.type.name !== 'columns') return false;
                    currentLayout = updatedNode.attrs.layout || 'equal-2';
                    const newCfg = COLUMN_LAYOUTS[currentLayout] || COLUMN_LAYOUTS['equal-2'];
                    applyGrid();
                    header.querySelector('span').textContent = newCfg.label;
                    return true;
                },
                destroy() {
                    mql.removeEventListener('change', applyGrid);
                },
            };
        };
    },
});

/* ================================================================
   Code block NodeView (copy button + language selector)
   ================================================================ */

function CodeBlockView({ node, updateAttributes }) {
    const [copied, setCopied] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(node.textContent).then(() => {
            setCopied(true);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setCopied(false), 2000);
        }).catch(() => {});
    }, [node]);

    return (
        <NodeViewWrapper data-code-block="" className="my-3 rounded-xl overflow-hidden border border-stone-800 bg-[#1c1917]">
            <div className="flex items-center justify-between px-3 py-1.5 bg-[#292524] border-b border-stone-700 select-none" contentEditable={false}>
                <select
                    value={node.attrs.language || ''}
                    onChange={(e) => updateAttributes({ language: e.target.value })}
                    className="bg-[#1c1917] text-stone-400 border border-stone-700 rounded-[5px] px-2 py-0.5 text-xs font-mono cursor-pointer outline-none focus:border-blue-500"
                >
                    <option value="">auto</option>
                    {CODE_LANGUAGES.map((lang) => (
                        <option key={lang} value={lang}>{lang}</option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1 bg-transparent text-stone-400 border-none rounded-[5px] px-2.5 py-0.5 text-xs cursor-pointer transition-colors hover:bg-stone-700 hover:text-stone-100"
                >
                    {copied
                        ? <><Check size={12} /> Copié</>
                        : <><Copy size={12} /> Copier</>
                    }
                </button>
            </div>
            <pre className="m-0 px-4 py-3.5 overflow-x-auto font-mono text-sm leading-relaxed text-stone-200 bg-[#1c1917]" spellCheck={false}>
                <NodeViewContent as="code" className="font-[inherit] text-[inherit] bg-transparent p-0" />
            </pre>
        </NodeViewWrapper>
    );
}

/* ================================================================
   Inline date node extension + NodeView
   ================================================================ */

function formatInlineDate(dateStr) {
    if (!dateStr) return '';
    try {
        const d = new Date(dateStr + 'T00:00:00');
        if (isNaN(d.getTime())) return dateStr;
        return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
    } catch {
        return dateStr;
    }
}

const InlineDateNode = Node.create({
    name: 'inlineDate',
    inline: true,
    group: 'inline',
    atom: true,
    selectable: true,

    addAttributes() {
        return {
            date: {
                default: new Date().toISOString().slice(0, 10),
                parseHTML: (el) => el.getAttribute('data-inline-date') || new Date().toISOString().slice(0, 10),
                renderHTML: (attrs) => ({ 'data-inline-date': attrs.date }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-inline-date]' }];
    },

    renderHTML({ node }) {
        return ['span', { 'data-inline-date': node.attrs.date }, formatInlineDate(node.attrs.date)];
    },

    addNodeView() {
        return ReactNodeViewRenderer(InlineDateView);
    },

    addInputRules() {
        return [
            nodeInputRule({
                find: /\/\/\s$/,
                type: this.type,
                getAttributes: () => ({ date: new Date().toISOString().slice(0, 10) }),
            }),
        ];
    },
});

function InlineDateView({ node, updateAttributes, editor }) {
    const inputRef = useRef(null);
    const isEditable = editor?.isEditable;

    const handleClick = useCallback(() => {
        if (!isEditable) return;
        inputRef.current?.showPicker?.();
    }, [isEditable]);

    const handleChange = useCallback((e) => {
        if (e.target.value) {
            updateAttributes({ date: e.target.value });
        }
    }, [updateAttributes]);

    return (
        <NodeViewWrapper as="span" className="inline relative">
            <span
                onClick={handleClick}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-sm border border-blue-200 ${isEditable ? 'cursor-pointer hover:bg-blue-100' : ''}`}
            >
                <Calendar size={13} className="shrink-0" />
                <span>{formatInlineDate(node.attrs.date)}</span>
            </span>
            {isEditable && (
                <input
                    ref={inputRef}
                    type="date"
                    value={node.attrs.date || ''}
                    onChange={handleChange}
                    className="absolute left-0 top-full opacity-0 w-px h-px overflow-hidden pointer-events-none"
                    tabIndex={-1}
                />
            )}
        </NodeViewWrapper>
    );
}

/* ================================================================
   Extension builder
   ================================================================ */

/**
 * Assembles the full TipTap extension array based on enabled features.
 * Must be memoized at call-site (useMemo) — creates new objects every call.
 *
 * @param {string[]} allowedFeatures - Feature keys (e.g. 'bold', 'table', 'columns').
 * @param {string} placeholder - Empty-state placeholder text.
 * @returns {Extension[]} TipTap extensions ready for useEditor().
 */
function buildExtensions(allowedFeatures, placeholder) {
    const features = new Set(allowedFeatures ?? DEFAULT_FEATURES);
    const starterKitOptions = {};

    const toggleableFeatures = [
        'bold', 'italic', 'strike', 'code',
        'blockquote', 'hardBreak', 'horizontalRule',
    ];
    toggleableFeatures.forEach((f) => {
        if (!features.has(f)) starterKitOptions[f] = false;
    });

    starterKitOptions.codeBlock = false;
    starterKitOptions.link = false;
    starterKitOptions.underline = false;

    // Drop-position indicator shown while dragging a block. prosemirror-dropcursor
    // defaults to an opaque BLACK bar — recolour it a subtle grey. (Its height is
    // also capped to a thin line by the .prosemirror-dropcursor-block CSS rule.)
    starterKitOptions.dropcursor = { color: '#64748b', width: 2 };

    if (features.has('heading')) {
        starterKitOptions.heading = { levels: [1, 2, 3, 4, 5, 6] };
    } else {
        starterKitOptions.heading = false;
    }

    if (!features.has('bulletList')) starterKitOptions.bulletList = false;
    if (!features.has('orderedList')) starterKitOptions.orderedList = false;
    if (!features.has('bulletList') && !features.has('orderedList') && !features.has('taskList')) {
        starterKitOptions.listItem = false;
    }

    const extensions = [StarterKit.configure(starterKitOptions)];

    if (features.has('heading')) {
        extensions.push(HeadingAnchors);
    }

    if (features.has('codeBlock')) {
        extensions.push(
            CodeBlockLowlight.extend({
                addNodeView() {
                    return ReactNodeViewRenderer(CodeBlockView);
                },
            }).configure({ lowlight })
        );
    }

    if (features.has('link')) {
        extensions.push(Link.configure({
            autolink: true,
            openOnClick: false,
            HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
        }));
    }

    if (features.has('underline')) extensions.push(Underline);
    if (features.has('subscript')) extensions.push(Subscript);
    if (features.has('superscript')) extensions.push(Superscript);

    if (features.has('image')) {
        extensions.push(ImageResize.configure({ inline: false, allowBase64: true, minWidth: 50 }));
    }

    if (features.has('textAlign')) {
        extensions.push(TextAlign.configure({
            types: ['heading', 'paragraph'],
            alignments: ['left', 'center', 'right', 'justify'],
        }));
    }

    if (features.has('highlight')) {
        extensions.push(Highlight.configure({ multicolor: true }));
    }

    extensions.push(TextStyleKit.configure({
        fontFamily: { types: ['textStyle'] },
        fontSize: { types: ['textStyle'] },
        lineHeight: { types: ['textStyle'] },
        color: features.has('color') ? { types: ['textStyle'] } : false,
        backgroundColor: false,
    }));

    if (features.has('table')) {
        const CustomTableCell = TableCell.extend({
            addAttributes() {
                return {
                    ...this.parent?.(),
                    backgroundColor: {
                        default: null,
                        parseHTML: (el) => el.style.backgroundColor || null,
                        renderHTML: (attrs) => {
                            if (!attrs.backgroundColor) return {};
                            return { style: `background-color: ${attrs.backgroundColor}` };
                        },
                    },
                };
            },
        });
        const CustomTableHeader = TableHeader.extend({
            addAttributes() {
                return {
                    ...this.parent?.(),
                    backgroundColor: {
                        default: null,
                        parseHTML: (el) => el.style.backgroundColor || null,
                        renderHTML: (attrs) => {
                            if (!attrs.backgroundColor) return {};
                            return { style: `background-color: ${attrs.backgroundColor}` };
                        },
                    },
                };
            },
        });
        extensions.push(
            Table.configure({ resizable: true }),
            TableRow,
            CustomTableHeader,
            CustomTableCell,
        );
    }

    if (features.has('taskList')) {
        extensions.push(
            TaskList,
            TaskItem.configure({ nested: true }),
        );
    }

    if (features.has('callout')) {
        extensions.push(CalloutNode);
    }

    if (features.has('details')) {
        extensions.push(DetailsNode);
    }

    if (features.has('columns')) {
        extensions.push(ColumnsNode, ColumnNode);
    }

    if (features.has('inlineDate')) {
        extensions.push(InlineDateNode);
    }

    if (features.has('statusBadge')) {
        extensions.push(StatusBadgeNode);
    }

    if (features.has('emoji')) {
        extensions.push(Emoji.configure({
            enableEmoticons: true,
            suggestion: {
                items: ({ query, editor: ed }) => {
                    const all = ed?.storage?.emoji?.emojis ?? [];
                    if (!query) return buildEmojiDefaults(all);
                    const q = query.toLowerCase();
                    return all.filter((e) =>
                        e.name.includes(q)
                        || e.shortcodes?.some((s) => s.includes(q))
                        || e.tags?.some((t) => t.includes(q))
                    ).slice(0, 50);
                },
                render: createEmojiSuggestionRenderer,
            },
        }));
    }

    extensions.push(Typography);

    extensions.push(SlashCommands.configure({
        suggestion: {
            render: createSlashSuggestionRenderer,
        },
    }));

    if (features.has('mention')) {
        extensions.push(Mention.configure({
            HTMLAttributes: { class: 'mention' },
            renderText({ node }) {
                return `@${node.attrs.label ?? node.attrs.id}`;
            },
            suggestion: {
                char: '@',
                allowSpaces: false,
                items: ({ query }) => fetchMentionUsers(query),
                render: createMentionSuggestionRenderer,
            },
        }));
    }

    extensions.push(Placeholder.configure({
        placeholder: placeholder || 'Tapez « / » pour les commandes',
    }));

    return extensions;
}

/* ================================================================
   Main editor component
   ================================================================ */

// Resize bounds — MUST mirror min-height/max-height in RichTextField.module.css.
const RESIZE_MIN = 120;
const RESIZE_MAX = 480;
const RESIZE_STEP = 24;

const clampEditorHeight = (h) => Math.min(RESIZE_MAX, Math.max(RESIZE_MIN, h));

// Read the CURRENT min-height, not the rendered box: `.ProseMirror` inherits
// min-height and the area adds its padding on top, so a rect-based origin would
// make the field jump by that padding on the first drag move.
const currentMinHeight = (el) =>
    (el ? parseFloat(window.getComputedStyle(el).minHeight) || RESIZE_MIN : RESIZE_MIN);

/**
 * ReactiveJSON component: WYSIWYG rich-text field backed by TipTap.
 *
 * YAML props: variant, placeholder, allowedFeatures, uploadUrl,
 *             dataLocation, defaultFieldValue, label.
 * Variants: 'seamless' (borderless, minimal chrome) or default (card-like).
 */
export const RichTextField = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const attributes = useEvaluatedAttributes(props.attributes ?? {});
    const inputAttributes = useEvaluatedAttributes(props.inputAttributes ?? {});
    const labelAttributes = useEvaluatedAttributes(props.labelAttributes ?? {});

    const { formData, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: props.defaultFieldValue ?? '',
        globalDataContext,
        templateContext,
    });

    const allowedFeatures = props.allowedFeatures ?? DEFAULT_FEATURES;
    const placeholder = props.placeholder || '';
    const isSeamless = props.variant === 'seamless';
    // Guards against a destructive sync loop: when the editor calls onUpdate,
    // the parent form re-renders, which would push the same HTML back into
    // setContent(), erasing the cursor position. This flag lets onUpdate()
    // skip the external formData→editor path for its own writes.
    const isInternalUpdateRef = useRef(false);

    const resolvedUploadUrl = evaluateTemplateValue({
        valueToEvaluate: props.uploadUrl,
        globalDataContext, templateContext,
    }) || '';

    const resolvedEntityId = evaluateTemplateValue({
        valueToEvaluate: props.entityId,
        globalDataContext, templateContext,
    }) || null;

    const resolvedMaxFileSize = props.maxFileSize
        ? Number(evaluateTemplateValue({ valueToEvaluate: props.maxFileSize, globalDataContext, templateContext })) || undefined
        : undefined;

    const { uploadFile } = useEditorFileUpload(resolvedUploadUrl, resolvedEntityId, { maxSize: resolvedMaxFileSize });
    const uploadFileRef = useRef(uploadFile);
    uploadFileRef.current = uploadFile;

    const resolvedAttachedFiles = props.attachedFilesLocation
        ? evaluateTemplateValue({ valueToEvaluate: props.attachedFilesLocation, globalDataContext, templateContext })
        : null;

    const { cleanupOrphans } = useOrphanFileCleanup(resolvedUploadUrl, resolvedEntityId);

    const findNodePos = useCallback((doc, predicate) => {
        let result = null;
        doc.descendants((node, pos) => {
            if (result !== null) return false;
            if (predicate(node)) { result = pos; return false; }
        });
        return result;
    }, []);

    const insertTempError = useCallback((editor, errorText) => {
        const marker = `⚠ ${errorText}`;
        editor.chain().focus().insertContent(`<p><em>${marker}</em></p>`).run();
        setTimeout(() => {
            if (editor.isDestroyed) return;
            const pos = findNodePos(editor.state.doc,
                (n) => n.isText && n.text?.includes(marker));
            if (pos !== null) {
                const parentFrom = editor.state.doc.resolve(pos).before();
                const parentTo = parentFrom + editor.state.doc.resolve(pos).parent.nodeSize;
                editor.chain().deleteRange({ from: parentFrom, to: parentTo }).run();
            }
        }, 8000);
    }, [findNodePos]);

    const handleEditorFileUpload = useCallback(async (editor, file, pos) => {
        const isImage = file.type.startsWith('image/');

        if (isImage) {
            const objectUrl = URL.createObjectURL(file);
            editor.chain().focus().insertContentAt(pos ?? editor.state.selection.anchor, {
                type: 'imageResize',
                attrs: { src: objectUrl, alt: file.name },
            }).run();

            try {
                const result = await uploadFileRef.current(file);
                if (!editor.isDestroyed) {
                    const nodePos = findNodePos(editor.state.doc,
                        (n) => n.type.name === 'imageResize' && n.attrs.src === objectUrl);
                    if (nodePos !== null) {
                        editor.chain()
                            .setNodeSelection(nodePos)
                            .updateAttributes('imageResize', { src: result.url })
                            .run();
                    }
                }
            } catch (err) {
                if (!editor.isDestroyed) {
                    const nodePos = findNodePos(editor.state.doc,
                        (n) => n.type.name === 'imageResize' && n.attrs.src === objectUrl);
                    if (nodePos !== null) {
                        editor.chain().setNodeSelection(nodePos).deleteSelection().run();
                    }
                    insertTempError(editor, `Échec de l'upload de ${file.name} — ${err.message}`);
                }
            } finally {
                URL.revokeObjectURL(objectUrl);
            }
        } else {
            const loadingLabel = `⏳ ${file.name}`;
            editor.chain().focus().insertContentAt(pos ?? editor.state.selection.anchor,
                `<p><em>${loadingLabel}</em></p>`
            ).run();

            try {
                const result = await uploadFileRef.current(file);
                if (!editor.isDestroyed) {
                    const sizeKB = result.filesize ? `${(result.filesize / 1024).toFixed(0)} KB` : '';
                    const linkHtml = `<a href="${result.url}" target="_blank" rel="noopener noreferrer">${result.filename}${sizeKB ? ` (${sizeKB})` : ''}</a>`;
                    const nodePos = findNodePos(editor.state.doc,
                        (n) => n.isText && n.text?.includes(loadingLabel));
                    if (nodePos !== null) {
                        const parentFrom = editor.state.doc.resolve(nodePos).before();
                        const parentTo = parentFrom + editor.state.doc.resolve(nodePos).parent.nodeSize;
                        editor.chain().deleteRange({ from: parentFrom, to: parentTo })
                            .insertContentAt(parentFrom, linkHtml).run();
                    }
                }
            } catch (err) {
                if (!editor.isDestroyed) {
                    const nodePos = findNodePos(editor.state.doc,
                        (n) => n.isText && n.text?.includes(loadingLabel));
                    if (nodePos !== null) {
                        const parentFrom = editor.state.doc.resolve(nodePos).before();
                        const parentTo = parentFrom + editor.state.doc.resolve(nodePos).parent.nodeSize;
                        editor.chain().deleteRange({ from: parentFrom, to: parentTo }).run();
                    }
                    insertTempError(editor, `Échec de l'upload de ${file.name} — ${err.message}`);
                }
            }
        }
    }, [findNodePos, insertTempError]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const featuresKey = useMemo(() => JSON.stringify(allowedFeatures), [JSON.stringify(allowedFeatures)]);
    const hasUpload = !!resolvedUploadUrl;

    useEffect(() => {
        if (hasUpload) {
            setSlashContext({ uploadHandler: handleEditorFileUpload });
            return () => setSlashContext({ uploadHandler: null });
        }
    }, [hasUpload, handleEditorFileUpload]);

    const extensions = useMemo(() => {
        const base = buildExtensions(allowedFeatures, placeholder);
        if (hasUpload) {
            base.push(FileHandler.configure({
                allowedMimeTypes: [
                    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
                    'application/pdf',
                    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                    'application/zip', 'text/plain',
                ],
                onDrop: (currentEditor, files, pos) => {
                    for (const file of files) {
                        handleEditorFileUpload(currentEditor, file, pos);
                    }
                },
                onPaste: (currentEditor, files) => {
                    for (const file of files) {
                        handleEditorFileUpload(currentEditor, file);
                    }
                },
            }));
        }
        return base;
    }, [featuresKey, placeholder, hasUpload, handleEditorFileUpload]);

    const editor = useEditor({
        extensions,
        content: formData ?? props.defaultFieldValue ?? '',
        immediatelyRender: true,
        shouldRerenderOnTransaction: false,
        editorProps: {
            attributes: { class: 'prose prose-stone max-w-none' },
        },
        onUpdate: ({ editor: ed }) => {
            const html = ed.getHTML();
            const isEmpty = html === '<p></p>' || html === '';
            isInternalUpdateRef.current = true;
            globalDataContext.updateData(isEmpty ? '' : html, formDataPath);
        },
    });

    // Fix the black drag-ghost rectangle. The editor's block drag (DragHandle
    // grip and NodeView paths) hands a bare, full-width transparent wrapper to
    // dataTransfer.setDragImage() — browsers snapshot a transparent element as a
    // black box, and Chromium additionally fades wide drag images to transparent
    // (also black). The extension exposes no option and overriding setDragImage
    // isn't reliable; but the LAST setDragImage call wins. So a document-level
    // listener (fires last, after the editor's own handler on a descendant),
    // scoped to THIS editor's active drag via view.dragging, replaces the image
    // with a clean, narrow card (narrow ⇒ no Chromium fade ⇒ no black).
    useEffect(() => {
        if (!editor) return;
        const onDragStart = (e) => {
            // Scope to drags coming from this editor (grip has class .drag-handle;
            // block/text drags originate inside view.dom). No view.dragging guard.
            const t = e.target;
            if (!(t instanceof Element)) return;
            const v = editor.view;
            const inEditor = t.closest('.drag-handle')
                || (v && v.dom && (v.dom.contains(t) || (v.dom.parentElement && v.dom.parentElement.contains(t))));
            if (!inEditor) return;
            const dt = e.dataTransfer;
            if (!dt || typeof dt.setDragImage !== 'function') return;
            let label = 'Bloc';
            try {
                const { from, to } = v.state.selection;
                const text = v.state.doc.textBetween(from, to, ' ', ' ').trim();
                if (text) label = text.length > 60 ? `${text.slice(0, 60)}…` : text;
            } catch { /* keep the generic label */ }
            const ghost = document.createElement('div');
            ghost.textContent = label;
            Object.assign(ghost.style, {
                position: 'fixed', top: '-9999px', left: '-9999px',
                maxWidth: '280px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                padding: '7px 12px 7px 10px', background: '#ffffff', color: '#1e293b',
                border: '1px solid #cbd5e1', borderLeft: '3px solid #64748b', borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(15, 23, 42, 0.22)',
                font: '500 13px ui-sans-serif, system-ui, sans-serif',
                opacity: '0.97', pointerEvents: 'none', zIndex: '2147483647',
            });
            document.body.appendChild(ghost);
            try { dt.setDragImage(ghost, 12, 12); } catch { /* noop */ }
            setTimeout(() => { try { ghost.remove(); } catch { /* noop */ } }, 0);
        };
        document.addEventListener('dragstart', onDragStart);
        return () => document.removeEventListener('dragstart', onDragStart);
    }, [editor]);

    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        if (isInternalUpdateRef.current) {
            isInternalUpdateRef.current = false;
            return;
        }
        const incoming = formData ?? props.defaultFieldValue ?? '';
        const current = editor.getHTML();
        const normalizedIncoming = incoming || '';
        const normalizedCurrent = current === '<p></p>' ? '' : current;
        if (normalizedIncoming !== normalizedCurrent) {
            editor.commands.setContent(incoming, false);
        }
    }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

    const prevAttachedFilesRef = useRef(null);
    useEffect(() => {
        if (!editor || !resolvedAttachedFiles) return;
        const prev = prevAttachedFilesRef.current;
        prevAttachedFilesRef.current = resolvedAttachedFiles;
        if (prev === null) return;
        if (prev === resolvedAttachedFiles) return;
        const bodyHtml = editor.getHTML();
        if (bodyHtml && bodyHtml !== '<p></p>') {
            cleanupOrphans(bodyHtml, resolvedAttachedFiles);
        }
    }, [resolvedAttachedFiles, editor, cleanupOrphans]);

    // ── User-resizable height ────────────────────────────────────────────
    // The grip writes min-height, never height: the size the user picks is a
    // FLOOR, so the editor keeps growing with its content up to RESIZE_MAX
    // (then scrolls). Deliberately not persisted — resets on every mount.
    const editorAreaRef = useRef(null);
    const dragRef = useRef(null);
    const [userHeight, setUserHeight] = useState(null);

    const handleGripPointerDown = useCallback((e) => {
        if (!editorAreaRef.current) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        dragRef.current = {
            startY: e.clientY,
            startHeight: currentMinHeight(editorAreaRef.current),
        };
    }, []);

    const handleGripPointerMove = useCallback((e) => {
        if (!dragRef.current) return;
        const { startY, startHeight } = dragRef.current;
        setUserHeight(clampEditorHeight(startHeight + (e.clientY - startY)));
    }, []);

    const handleGripPointerUp = useCallback((e) => {
        if (!dragRef.current) return;
        dragRef.current = null;
        if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
    }, []);

    // Keyboard parity: a drag-only grip would fail WCAG 2.1.1 (keyboard).
    const handleGripKeyDown = useCallback((e) => {
        const current = currentMinHeight(editorAreaRef.current);
        const next = {
            ArrowDown: current + RESIZE_STEP,
            ArrowUp: current - RESIZE_STEP,
            Home: RESIZE_MIN,
            End: RESIZE_MAX,
        }[e.key];
        if (next === undefined) return;
        e.preventDefault();
        setUserHeight(clampEditorHeight(next));
    }, []);

    const { class: wrapperClass, ...wrapperRest } = attributes ?? {};
    const { class: editorClass, style: editorStyle, ...editorRest } = inputAttributes ?? {};
    const { class: labelClass, ...labelRest } = labelAttributes ?? {};

    return (
        <ActionDependant {...props}>
            <div className={wrapperClass ?? undefined} {...wrapperRest}>
                {props.label && (
                    <label className={labelClass ?? undefined} {...labelRest}>
                        {props.label}
                    </label>
                )}
                <div className={isSeamless
                    ? "rounded-xl overflow-hidden"
                    : "border border-stone-300 rounded-xl overflow-hidden bg-white transition-all focus-within:border-blue-500 focus-within:ring-3 focus-within:ring-blue-500/15"
                }>
                    <div
                        ref={editorAreaRef}
                        className={[styles.editorArea, editorClass].filter(Boolean).join(' ')}
                        style={{
                            ...(editorStyle ?? {}),
                            ...(userHeight ? { minHeight: `${userHeight}px` } : {}),
                        }}
                        {...editorRest}
                        onClick={(e) => {
                            if (!editor || editor.isDestroyed) return;
                            const pm = e.currentTarget.querySelector('.ProseMirror');
                            if (pm && !pm.contains(e.target)) {
                                editor.commands.focus('end');
                            }
                        }}
                    >
                        <EditorContent editor={editor} />
                    </div>
                    <div
                        role="separator"
                        aria-orientation="horizontal"
                        aria-label="Redimensionner la hauteur du champ"
                        aria-valuenow={Math.round(userHeight ?? RESIZE_MIN)}
                        aria-valuemin={RESIZE_MIN}
                        aria-valuemax={RESIZE_MAX}
                        tabIndex={0}
                        className={styles.resizeGrip}
                        onPointerDown={handleGripPointerDown}
                        onPointerMove={handleGripPointerMove}
                        onPointerUp={handleGripPointerUp}
                        onPointerCancel={handleGripPointerUp}
                        onKeyDown={handleGripKeyDown}
                    >
                        <span className={styles.resizeGripBar} aria-hidden="true" />
                    </div>
                    {editor && <EditorMenus editor={editor} allowedFeatures={allowedFeatures} onUploadFile={hasUpload ? handleEditorFileUpload : null} />}
                </div>
            </div>
        </ActionDependant>
    );
};
